import './CaseResolutionShowDetail.scss';

import { Button, Card, Flex, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import LinkTextField from '@/components/general/LinkTextField';
import { ShowInfoItem } from '@/components/general/ShowInfo/ShowInfo';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import ServiceOrderStatusTag from '@/components/serviceOrder/ServiceOrderStatusTag';
import TicketLogisticStateTag from '@/components/ticket/TicketLogisticStateTag';
import { COMPANY_URLS, MACHINES_URLS, TECHNICIAN_URLS } from '@/constants/urls';
import Company from '@/model/General/Company';
import { Technician } from '@/model/General/Technician';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPMENT_TYPE } from '@/model/Logistic/Shipment/ShipmentType';
import { ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { Ticket } from '@/model/Ticket/Ticket';
import { RESOURCES } from '@/Resources';

interface CaseResolutionShowDetailProps {
  ticket: Ticket | undefined;
  shipments: Shipment[];
  receptions: Reception[];
  serviceOrders: ServiceOrder[];
  logisticCompanies: Company[];
  paymentCompanies: Company[];
  refetch: () => void;
}

const CaseResolutionShowDetail = (props: CaseResolutionShowDetailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ticket, shipments, receptions, serviceOrders, logisticCompanies, paymentCompanies } =
    props;

  return (
    <Card className="CaseResolutionShowDetail">
      <div className="CaseResolutionShowDetailTitle">{t('Detalles del caso')}</div>
      {ticket?.logistics_state != undefined && (
        <ShowInfoItem label={t('Estado del caso')} inline className="CaseResolutionShowDetailItem">
          <TicketLogisticStateTag ticketLogisticState={ticket?.logistics_state} />
        </ShowInfoItem>
      )}
      <ShowInfoItem label={t('Ticket asociado')} inline className="CaseResolutionShowDetailItem">
        <LinkTextField
          local
          to={`/${RESOURCES.TICKETS}/show/${ticket?.id}`}
          label={`${ticket?.id} - ${ticket?.ticket_title_str}`}
        />
      </ShowInfoItem>
      <ShowInfoItem label={t('Empresa del ticket')} inline className="CaseResolutionShowDetailItem">
        <LinkTextField
          admin
          to={`${COMPANY_URLS.ADMIN_EMPRESA}/${ticket?.empresa}`}
          label={ticket?.empresa_str || ''}
        />
      </ShowInfoItem>
      <ShowInfoItem label={t('Máquina')} inline className="CaseResolutionShowDetailItem">
        <LinkTextField
          admin
          to={`${MACHINES_URLS.ADMIN_MOVIL}/${ticket?.movil}`}
          label={`${ticket?.movil_str} (${ticket?.movil})`}
        />
      </ShowInfoItem>

      <ShowInfoItem
        label={t('Empresas involucradas en la logística')}
        className="CaseResolutionShowDetailItem">
        <ul>
          {logisticCompanies.length > 0 ? (
            logisticCompanies.map((company, index) => (
              <li key={company.id}>
                <LinkTextField
                  admin
                  to={`${COMPANY_URLS.ADMIN_EMPRESA}/${company.id}`}
                  label={company.full_name}
                />
                {index < logisticCompanies.length - 1 ? ', ' : ''}
              </li>
            ))
          ) : (
            <span> - </span>
          )}
        </ul>
      </ShowInfoItem>
      <ShowInfoItem
        label={t('Empresas involucradas en la facturación')}
        className="CaseResolutionShowDetailItem">
        <ul>
          {paymentCompanies.length > 0 ? (
            paymentCompanies.map((company, index) => (
              <li key={company.id}>
                <LinkTextField
                  admin
                  to={`${COMPANY_URLS.ADMIN_EMPRESA}/${company.id}`}
                  label={company.full_name}
                />
                {index < logisticCompanies.length - 1 ? ', ' : ''}
              </li>
            ))
          ) : (
            <span> - </span>
          )}
        </ul>
      </ShowInfoItem>
      {shipments.length > 0 && (
        <ShowInfoItem label={t('Envíos')} className="CaseResolutionShowDetailItem">
          <ul>
            {shipments.map(shipment => (
              <li key={shipment.id}>
                <Flex gap={8}>
                  <LinkTextField
                    local
                    to={`/${RESOURCES.SHIPMENTS}/show/${shipment.id}`}
                    label={`${t('Envío')} #${shipment.id}`}
                  />
                  -{' '}
                  {shipment.status != undefined && (
                    <Flex wrap gap={4}>
                      <PackageStateTag packageStatus={shipment.status} />
                      {shipment?.tipo == SHIPMENT_TYPE.AUTOMATICO && (
                        <Tag color="geekblue">{t('Automático')}</Tag>
                      )}
                    </Flex>
                  )}
                </Flex>
              </li>
            ))}
          </ul>
        </ShowInfoItem>
      )}

      {receptions.length > 0 && (
        <ShowInfoItem label={t('Recepciones')} className="CaseResolutionShowDetailItem">
          <ul>
            {receptions.map(reception => (
              <li key={reception.id}>
                <Flex gap={8}>
                  <LinkTextField
                    local
                    to={`/${RESOURCES.RECEPTIONS}/show/${reception.id}`}
                    label={`${t('Recepción')} #${reception.id}`}
                  />
                  -{' '}
                  {reception.status != undefined && (
                    <Flex wrap gap={4}>
                      <PackageStateTag packageStatus={reception.status} />
                      {reception?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
                        <Tag color="geekblue">{t('Automática')}</Tag>
                      )}
                    </Flex>
                  )}
                </Flex>
              </li>
            ))}
          </ul>
        </ShowInfoItem>
      )}
      {serviceOrders.length > 0 && (
        <ShowInfoItem label={t('Órdenes de servicio')} className="CaseResolutionShowDetailItem">
          <ul>
            {serviceOrders.map(serviceOrder => (
              <li key={serviceOrder.id}>
                <Flex gap={8}>
                  <LinkTextField
                    local
                    to={`/${RESOURCES.SERVICE_ORDERS}/show/${serviceOrder.id}`}
                    label={`${t('Orden de servicio')} #${serviceOrder.id}`}
                  />
                  -{' '}
                  <LinkTextField
                    admin
                    to={`/${TECHNICIAN_URLS.ADMIN}/${(serviceOrder.technician as unknown as Technician).id}`}
                    label={`${(serviceOrder.technician as unknown as Technician).nombre}`}
                  />{' '}
                  {serviceOrder.status != undefined && (
                    <Flex wrap gap={4}>
                      <ServiceOrderStatusTag serviceOrderStatus={serviceOrder.status} />
                    </Flex>
                  )}
                </Flex>
              </li>
            ))}
          </ul>
        </ShowInfoItem>
      )}

      <div className="CaseResolutionShowDetailFooter">
        <Button
          onClick={() => void navigate(`/${RESOURCES.SHIPMENTS}/create?ticket=${ticket?.id}`)}>
          {t('Crear envío')}
        </Button>
        <Button
          onClick={() => void navigate(`/${RESOURCES.RECEPTIONS}/create?ticket=${ticket?.id}`)}>
          {t('Crear recepción')}
        </Button>
      </div>
    </Card>
  );
};

export default CaseResolutionShowDetail;
